/**
 * friendlyError.js
 *
 * RULE: the UI may only show the fixed strings below, chosen by failure kind.
 * Never render err.message, body.detail, a response body, String(err), or any
 * server / JavaScript error text.
 *
 *   const { kind, code, message, canRetry, retryAfter } = await toFriendlyError({ res, err });
 *
 * `code` (e.g. CA-BUSY) is a developer code for logs and support. It is never rendered.
 *
 * - Pass `res` for an HTTP response that was not ok, `err` for a thrown error
 *   (fetch rejected, aborted, ...). It is async because it may read a busy body.
 * - It never throws: bodies are parsed inside try/catch, and a non-JSON body
 *   just means the kind is decided from the status alone.
 * - The only thing it logs is { code, status }: no bodies, ids or messages.
 */

export const FRIENDLY_COPY = Object.freeze({
  busy: 'Your casting suggestions are taking a little longer than usual. Please try again in a moment.',
  network: "We couldn't connect just now. Please check your internet connection and try again.",
  auth: 'For your security, please sign in again to continue.',
  forbidden: "This project isn't available on your account. Please check with the project owner.",
  invalid: "We couldn't process that request. Please review the details and try again.",
  rate: 'Please give us a moment, then try again.',
  server: "We're experiencing a brief interruption. Please try again in a moment.",
});

// Developer codes: for logs and support only. They are NEVER rendered on screen.
export const FRIENDLY_CODES = Object.freeze({
  busy: 'CA-BUSY',
  network: 'CA-NET',
  auth: 'CA-AUTH',
  forbidden: 'CA-FORBID',
  invalid: 'CA-INVALID',
  rate: 'CA-RATE',
  server: 'CA-SERVER',
});

// Error boundary (CastingErrorBoundary.jsx) copy and code.
export const CRASH_CODE = 'CA-CRASH';
export const CRASH_COPY = "We're refreshing this page for you. Please tap Refresh to continue.";

const CAN_RETRY = Object.freeze({
  busy: true, network: true, rate: true, server: true,
  auth: false, forbidden: false, invalid: false,
});

const DEFAULT_RETRY_AFTER = 60;
const MIN_RETRY_AFTER = 15;
const MAX_RETRY_AFTER = 90;
const RATE_RETRY_AFTER = 15;

const clampRetry = (seconds) => Math.min(Math.max(seconds, MIN_RETRY_AFTER), MAX_RETRY_AFTER);

async function readJson(res) {
  try {
    return await res.json();
  } catch {
    return null; // non-JSON body, or body already consumed: never surfaces to the UI
  }
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

export async function toFriendlyError({ res, err } = {}) {
  let kind = 'server';
  let retryAfter = 0;
  const status = res ? res.status : undefined;

  if (res) {
    if (status === 503) {
      const body = await readJson(res);
      if (body && typeof body === 'object' && body.status === 'busy') {
        kind = 'busy';
        const hint = typeof body.retry_after === 'number' && Number.isFinite(body.retry_after)
          ? body.retry_after
          : DEFAULT_RETRY_AFTER;
        retryAfter = clampRetry(hint);
      }
      // any other 503 (not the app's busy response) stays "server"
    } else if (status === 504) {
      kind = 'busy';
      retryAfter = DEFAULT_RETRY_AFTER;
    } else if (status === 401) {
      kind = 'auth';
    } else if (status === 403) {
      kind = 'forbidden';
    } else if (status === 429) {
      kind = 'rate';
      retryAfter = RATE_RETRY_AFTER;
    } else if (status >= 400 && status < 500) {
      kind = 'invalid';
    }
    // 2xx/3xx here, other 5xx, or anything unexpected: "server"
  } else if (err && (err.name === 'AbortError' || err.name === 'TimeoutError')) {
    kind = 'busy';
    retryAfter = DEFAULT_RETRY_AFTER;
  } else if ((err instanceof TypeError) || isOffline()) {
    kind = 'network'; // fetch() rejects with a TypeError when the network is unreachable
  }

  const code = FRIENDLY_CODES[kind];
  console.warn('[casting] request failed', { code, status });

  return { kind, code, message: FRIENDLY_COPY[kind], canRetry: CAN_RETRY[kind], retryAfter };
}
